import React, { useState } from 'react'
import { gql, useQuery } from 'urql'
import { DateTime } from 'luxon'
import {
  Alert as MUIAlert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  MenuItem,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { Check, Close, Info, Settings } from '@mui/icons-material'
import { useURLParam } from '../actions'
import AppLink from '../util/AppLink'
import {
  Empty,
  LoadMore,
  Page,
  Panel,
  SearchBox,
  TimeFilter,
  useBrand,
  useTimeFilter,
} from './ui'
import { outcomeOf, useServiceKinds } from './reports'

// GoAlert's urql client suspends by default; render our own loading state.
const noSuspense = { suspense: false }

const listQuery = gql`
  query ErecordsReports($input: AlertSearchOptions) {
    alerts(input: $input) {
      nodes {
        id
        alertID
        status
        summary
        createdAt
        service {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

type Item = {
  id: string
  alertID: number
  status: string
  summary: string
  createdAt: string
  service: { id: string; name: string } | null
}

function OutcomeIcon({ summary }: { summary: string }): React.JSX.Element {
  const theme = useTheme()
  const c = useBrand()
  const o = outcomeOf(summary)
  const [color, bg, icon] =
    o === 'passed'
      ? [
          c.ok,
          theme.palette.mode === 'dark' ? '#1f3328' : '#e4f1e7',
          <Check key='i' fontSize='small' />,
        ]
      : o === 'failed'
        ? [
            theme.palette.error.main,
            c.accentSoft,
            <Close key='i' fontSize='small' />,
          ]
        : [c.muted, c.soft, <Info key='i' fontSize='small' />]
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '8px',
        display: 'grid',
        placeItems: 'center',
        color,
        bgcolor: bg,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
  )
}

function dayHeading(iso: string): string {
  const d = DateTime.fromISO(iso)
  const today = DateTime.local().startOf('day')
  if (d >= today) return 'Today'
  if (d >= today.minus({ days: 1 })) return 'Yesterday'
  return d.toFormat('cccc d LLLL')
}

function ManageDialog(props: {
  onClose: () => void
  kinds: ReturnType<typeof useServiceKinds>
}): React.JSX.Element {
  const c = useBrand()
  const [busy, setBusy] = useState('')
  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Report services</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
          Alerts from these services are informational: they still notify (e.g.
          post to Teams), then close by themselves after about two minutes and
          show here. Anything that reads like a failure (FAILED, ERROR, DOWN…)
          stays a normal alert.
        </Typography>
        {props.kinds.services.map((s) => (
          <Box
            key={s.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              py: 0.75,
              '& + &': { borderTop: `1px solid ${c.line}` },
            }}
          >
            <Typography sx={{ flex: 1 }}>{s.name}</Typography>
            <Switch
              checked={s.report}
              disabled={busy === s.id}
              onChange={(e) => {
                setBusy(s.id)
                props.kinds
                  .setReport(s.id, e.target.checked)
                  .finally(() => setBusy(''))
              }}
              inputProps={{ 'aria-label': `${s.name} is a report service` }}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}

function ReportChunk(props: {
  input: Record<string, unknown>
  cursor: string
  last: boolean
  onMore: (cursor: string) => void
}): React.JSX.Element {
  const c = useBrand()
  const [q] = useQuery({
    query: listQuery,
    variables: { input: { ...props.input, first: 50, after: props.cursor } },
    requestPolicy: 'cache-and-network',
    context: noSuspense,
  })
  const nodes: Item[] = q.data?.alerts?.nodes ?? []
  const info = q.data?.alerts?.pageInfo

  if (q.error) return <MUIAlert severity='error'>{q.error.message}</MUIAlert>
  if (!q.data)
    return <Typography sx={{ p: 2.5, color: c.muted }}>Loading…</Typography>
  if (!nodes.length && !props.cursor) return <Empty title='No reports yet' />

  let day: string | undefined
  return (
    <React.Fragment>
      {nodes.map((r) => {
        const d = dayHeading(r.createdAt)
        const heading = d !== day
        day = d
        const failed = outcomeOf(r.summary) === 'failed'
        return (
          <React.Fragment key={r.id}>
            {heading && (
              <Typography
                sx={{
                  px: 2.5,
                  pt: 1.5,
                  pb: 0.5,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: c.muted,
                  borderTop: `1px solid ${c.line}`,
                  '&:first-of-type': { borderTop: 'none' },
                }}
              >
                {d}
              </Typography>
            )}
            <AppLink
              to={`/alerts/${r.alertID}`}
              underline='none'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2.5,
                py: 1,
                color: c.ink,
                '&:hover': { bgcolor: c.hover },
              }}
            >
              <OutcomeIcon summary={r.summary} />
              <Typography
                sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500 }}
                noWrap
              >
                {r.summary}
              </Typography>
              {failed && r.status !== 'StatusClosed' && (
                <Chip
                  size='small'
                  color='error'
                  variant='outlined'
                  label='Needs attention under Alerts'
                />
              )}
              <Typography
                sx={{
                  fontSize: 12,
                  color: c.muted,
                  width: 160,
                  textAlign: 'right',
                  display: { xs: 'none', md: 'block' },
                }}
                noWrap
              >
                {r.service?.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: c.muted,
                  width: 48,
                  textAlign: 'right',
                }}
              >
                {DateTime.fromISO(r.createdAt).toFormat('HH:mm')}
              </Typography>
            </AppLink>
          </React.Fragment>
        )
      })}
      <LoadMore
        show={props.last && Boolean(info?.hasNextPage)}
        loading={q.fetching}
        onClick={() => props.onMore(info.endCursor)}
      />
    </React.Fragment>
  )
}

function ReportList(props: {
  input: Record<string, unknown>
}): React.JSX.Element {
  const [cursors, setCursors] = useState<string[]>([''])
  return (
    <React.Fragment>
      {cursors.map((cursor, i) => (
        <ReportChunk
          key={cursor || 'first'}
          input={props.input}
          cursor={cursor}
          last={i === cursors.length - 1}
          onMore={(next) => setCursors([...cursors, next])}
        />
      ))}
    </React.Fragment>
  )
}

// ReportsPage lists informational items (BVT runs and the like) from report
// services: no acknowledgement, they close themselves after notifying.
export default function ReportsPage(): React.JSX.Element {
  const c = useBrand()
  const [service, setService] = useURLParam<string>('service', '')
  const [search, setSearch] = useURLParam<string>('search', '')
  const [manage, setManage] = useState(false)
  const kinds = useServiceKinds()
  const reportIDs = kinds.services.filter((s) => s.report).map((s) => s.id)
  const ids = service ? [service] : reportIDs
  const tf = useTimeFilter()

  return (
    <Page
      title='Reports'
      subtitle='Information from test runs and other senders: nothing to acknowledge. Failures from these services still show up under Alerts.'
      actions={
        <Button
          variant='outlined'
          startIcon={<Settings />}
          onClick={() => setManage(true)}
        >
          Report services
        </Button>
      }
    >
      {manage && (
        <ManageDialog onClose={() => setManage(false)} kinds={kinds} />
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          select
          size='small'
          label='Service'
          value={service}
          onChange={(e) => setService(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value=''>All report services</MenuItem>
          <ListSubheader>Report services</ListSubheader>
          {kinds.services
            .filter((s) => s.report)
            .map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
        </TextField>
        <TimeFilter f={tf} />
        <Box sx={{ flex: 1 }} />
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder='Search reports'
        />
      </Box>

      <Panel flush>
        {kinds.loaded && reportIDs.length === 0 ? (
          <Empty
            title='No report services yet'
            text='Mark a service (for example BVT Test Runner) as a report service to see its runs here.'
            action={
              <Button variant='contained' onClick={() => setManage(true)}>
                Choose report services
              </Button>
            }
          />
        ) : kinds.loaded ? (
          <ReportList
            key={JSON.stringify([ids, search, tf.range])}
            // newest first: the default (status first) put open failures
            // above newer closed reports, breaking the day grouping
            input={{
              search,
              filterByServiceID: ids,
              sort: 'dateID',
              ...tf.range,
            }}
          />
        ) : (
          <Typography sx={{ p: 2.5, color: c.muted }}>Loading…</Typography>
        )}
      </Panel>
    </Page>
  )
}
